import { apiFetch } from './client';

export interface ResellerRow {
  id: string; name: string; slug: string; reseller_number: number; active: boolean;
  timezone: string; default_language: 'en' | 'he' | 'yi';
  /** Default dialing prefix ('+' + 1-3 digits). NULL = inherit the platform
   *  default. Migration 0113. */
  default_country_code?: string | null;
  recording_dial_in_e164: string | null;
  /** Reseller cap on per-customer recycle-bin retention (1-90). Migration 0059. */
  recording_recycle_bin_retention_days_max?: number;
  /** Reseller's price (cents) per 30-day retention unit per customer per
   *  month. 30 days = 1×, 60 = 2×, 90 = 3×. Migration 0059. */
  recording_recycle_bin_unit_price_cents?: number;
  created_at: string; updated_at: string;
}

interface CreateBody {
  name: string; slug: string; timezone?: string;
  defaultLanguage?: 'en' | 'he' | 'yi';
  firstOwner: {
    email: string; displayName: string | null; phone: string | null;
    method: 'email_invite' | 'manual_password'; manualPassword?: string;
  };
}

export const resellers = {
  list: (params: { search?: string; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.search) q.set('search', params.search);
    if (params.limit) q.set('limit', String(params.limit));
    return apiFetch<{ rows: ResellerRow[] }>(`/api/resellers?${q}`);
  },
  get: (id: string) => apiFetch<ResellerRow>(`/api/resellers/${id}`),
  /** Look up a single reseller by its 4-digit reseller_number. Used by
   *  the scoped URL layout's super-admin variant. */
  getByNumber: (resellerNumber: number) =>
    apiFetch<ResellerRow>(`/api/resellers/by-number/${resellerNumber}`),
  create: (body: CreateBody) =>
    apiFetch<{ resellerId: string; firstOwner: { userId: string | null; inviteId: string | null; inviteToken: string | null } }>(
      '/api/resellers', { method: 'POST', body: JSON.stringify(body) },
    ),
  update: (
    id: string,
    body: Partial<Pick<ResellerRow, 'name' | 'timezone' | 'active'>>
        & { defaultLanguage?: 'en' | 'he' | 'yi' }
        & { defaultCountryCode?: string | null }
        & { recordingRecycleBinRetentionDaysMax?: number }
        & { recordingRecycleBinUnitPriceCents?: number },
  ) =>
    apiFetch<ResellerRow>(`/api/resellers/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  remove: (id: string) => apiFetch<void>(`/api/resellers/${id}`, { method: 'DELETE' }),

  /** Set (or clear) the E.164 number that triggers the admin recording dial-in
   *  flow for this reseller. Pass null to unset. */
  setRecordingDialInE164: (id: string, e164: string | null) =>
    apiFetch<ResellerRow>(`/api/resellers/${id}/recording-dial-in`, {
      method: 'PATCH',
      body: JSON.stringify({ e164 }),
    }),
};
